/**
 * DashboardStatCard — Single cell in the 2×2 analytics grid.
 *
 * Icon + colour configuration is variant-driven. For the 'earnings' variant
 * the value is rendered as plain text with an accessibilityLabel override
 * because gradient text (background-clip) cannot convey meaning to screen readers.
 *
 * Shadow uses createBoxShadow from design-tokens so web/native are both handled.
 */

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  StyleProp,
  ViewStyle,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { createBoxShadow } from '../../../lib/design-tokens';

export type StatCardVariant = 'clients' | 'analyses' | 'earnings' | 'improving';

type IoniconsName = React.ComponentProps<typeof Ionicons>['name'];

interface VariantConfig {
  iconName: IoniconsName;
  iconWrapBg: string;
  iconColor: string;
  valueColor: string;
  isGradientValue?: boolean;
  gradientColors?: readonly [string, string];
}

const VARIANT_CONFIG: Record<StatCardVariant, VariantConfig> = {
  clients: {
    iconName: 'people-outline',
    iconWrapBg: '#EDE9FE',
    iconColor: '#7C3AED',
    valueColor: '#18181B',
  },
  analyses: {
    iconName: 'bar-chart-outline',
    iconWrapBg: '#FCE7F3',
    iconColor: '#EC4899',
    valueColor: '#18181B',
  },
  earnings: {
    iconName: 'cash-outline',
    iconWrapBg: '#CCFBF1',
    iconColor: '#14B8A6',
    valueColor: '#EC4899',
    isGradientValue: true,
    gradientColors: ['#EC4899', '#14B8A6'],
  },
  improving: {
    iconName: 'trending-up-outline',
    iconWrapBg: '#D1FAE5',
    iconColor: '#10B981',
    valueColor: '#10B981',
  },
};

const DELTA_COLOR_POSITIVE = '#10B981';
const DELTA_COLOR_NEGATIVE = '#EF4444';

interface DashboardStatCardProps {
  variant: StatCardVariant;
  value: string;
  label: string;
  delta?: string;
  deltaPositive?: boolean;
  accessibilityLabel: string;
  style?: StyleProp<ViewStyle>;
}

const cardShadow = createBoxShadow({
  color: '#A78BFA',
  offsetX: 0,
  offsetY: 4,
  blurRadius: 16,
  opacity: 0.10,
  elevation: 4,
});

export const DashboardStatCard: React.FC<DashboardStatCardProps> = ({
  variant,
  value,
  label,
  delta,
  deltaPositive,
  accessibilityLabel,
  style,
}) => {
  const config = VARIANT_CONFIG[variant];

  return (
    <View
      style={[styles.card, cardShadow as StyleProp<ViewStyle>, style]}
      accessible={true}
      accessibilityLabel={accessibilityLabel}
    >
      <View style={styles.topRow}>
        <View style={[styles.iconWrap, { backgroundColor: config.iconWrapBg }]}>
          <Ionicons name={config.iconName} size={16} color={config.iconColor} />
        </View>
        {delta !== undefined && (
          <Text
            style={[
              styles.delta,
              { color: deltaPositive ? DELTA_COLOR_POSITIVE : DELTA_COLOR_NEGATIVE },
            ]}
          >
            {delta}
          </Text>
        )}
      </View>
      {config.isGradientValue && Platform.OS === 'web' && config.gradientColors ? (
        <Text
          style={[
            styles.value,
            {
              background: `linear-gradient(135deg, ${config.gradientColors[0]}, ${config.gradientColors[1]})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            } as any,
          ]}
        >
          {value}
        </Text>
      ) : (
        <Text style={[styles.value, { color: config.valueColor }]}>
          {value}
        </Text>
      )}
      <Text style={styles.label}>{label}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: 'rgba(255,255,255,0.85)',
    borderRadius: 24,
    padding: 14,
  } as ViewStyle,
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  iconWrap: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  value: {
    fontSize: 24,
    fontWeight: '800',
    letterSpacing: -0.72,
    lineHeight: 28,
  },
  label: {
    fontSize: 11,
    fontWeight: '500',
    color: '#A1A1AA',
    marginTop: 2,
    lineHeight: 15,
  },
  delta: {
    fontSize: 11,
    fontWeight: '600',
  },
});
